/**
 * Category / split for one ledger line (C7; TMMAPP-0108; audit 06 § C7, the tmm-api review
 * amendment; MM-D-125 / 128, TMMAPI-0145 item 6; reference v2/10): a full re-split of the line's
 * parts inside this virtual account. Each row picks its envelope or Unassigned, takes an amount, a
 * percent (of the line's total in this virtual account) or "the rest" — one Rest per request — and a
 * category from **every** category of the virtual account, regardless of direction (MM-D-128), or
 * Uncategorized on every row, which sends `uncategorized: true` (TMMAPI-0152; TMMAPP-0114 B5).
 * "+ Add a row" lets the same envelope take a second category. "+ New category" shows Add category
 * (C8) in place of the rows — never a second dialog (DLG-005) — and returns with it selectable.
 *
 * Save is one `PUT …/lines/{tid}/split`; the total never changes (MM-D-114). `409
 * SPLIT_TOTAL_CHANGED` reloads the line, re-seeds the rows and says "This line changed — review and
 * save again." The previews follow the server's rule (fixed, then percent floored, then the rest)
 * and are previews only: the ledger refetches after a save.
 */
import { useId, useState } from 'react';
import { MoneyParts } from '@/components/MoneyText';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { allocationsApi, type SplitPartRequest } from '@/api/allocations';
import type { Category } from '@/api/categories';
import type { Envelope } from '@/api/envelopes';
import { ErrorCodes, isApiError } from '@/api/errors';
import { errorCopy } from '@/api/errorCopy';
import type { VirtualAccountRegisterLine } from '@/api/registers';
import type { VirtualAccountDetail } from '@/api/virtualAccounts';
import { DialogSubtitle } from '@/components/DialogSubtitle';
import { GuardedDialog } from '@/components/GuardedDialog';
import { CloseIcon } from '@/components/icons';
import { SubmitButton } from '@/components/SubmitButton';
import { ModeToggle } from '@/features/accounts/AllocateDestinations';
import type { AllocateMode } from '@/features/accounts/allocateRows';
import {
  formatCivilDate,
  formatMoneyMinor,
  minorToInputText,
  parseMoneyToMinor,
  percentToBp,
} from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { sameValues } from '@/shared/useDirty';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { categoricalFor } from '@/theme/categorical';
import { resolveTokens } from '@/theme/theme';
import { AddCategoryForm } from './AddCategoryDialog';
import { EMPTY_CATEGORY_DRAFT, isCategoryDraftDirty, type CategoryDraft } from './categoryDraft';
import { previewSplit } from './splitPreview';

const UNASSIGNED = 'unassigned';
const NONE = 'none';

interface SplitRow {
  key: string;
  /** An envelope id as text, or `unassigned`. */
  target: string;
  mode: AllocateMode;
  /** The magnitude the person typed; the line's sign applies. */
  value: string;
  /** A category id as text, or `none` (sent as `uncategorized: true`). */
  category: string;
}

function seed(line: VirtualAccountRegisterLine, currency: string, locale: string): SplitRow[] {
  return line.allocations.map((part, index) => ({
    key: `part-${part.id ?? index}`,
    target: part.unassigned ? UNASSIGNED : String(part.envelopeId ?? ''),
    mode: 'amount',
    value:
      part.amountMinor === null
        ? ''
        : minorToInputText(Math.abs(part.amountMinor), currency, locale),
    category: part.categoryId === null ? NONE : String(part.categoryId),
  }));
}

interface CategorySplitDialogProps {
  virtualAccount: VirtualAccountDetail;
  line: VirtualAccountRegisterLine;
  /** The virtual account's active envelopes, in display order. */
  envelopes: Envelope[];
  categories: Category[];
  onClose: () => void;
  onSaved: () => void | Promise<void>;
  /** Reload the ledger and answer this line as it is now (null when it left the ledger). */
  onStale: () => Promise<VirtualAccountRegisterLine | null>;
  onCategoryAdded: (category: Category) => void | Promise<void>;
  onSubscriptionRequired: () => void;
}

export function CategorySplitDialog({
  virtualAccount,
  line: initialLine,
  envelopes,
  categories,
  onClose,
  onSaved,
  onStale,
  onCategoryAdded,
  onSubscriptionRequired,
}: CategorySplitDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const fullScreen = useDialogFullScreen();
  const titleId = useId();
  const subtitleId = useId();
  const addTitleId = useId();
  const addSubtitleId = useId();
  const currency = virtualAccount.currency;
  const [line, setLine] = useState(initialLine);
  const [baseline, setBaseline] = useState(() => seed(initialLine, currency, locale));
  const [rows, setRows] = useState(baseline);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [adding, setAdding] = useState<CategoryDraft | null>(null);
  const [addBusy, setAddBusy] = useState(false);
  const [nextKey, setNextKey] = useState(0);

  const dirty = !sameValues(rows, baseline) || (adding !== null && isCategoryDraftDirty(adding));
  const guard = useDialogDirtyGuard(dirty, onClose, submitting || addBusy);

  const total = line.publishedMinor;
  const previews = previewSplit(
    rows,
    total,
    (text) => parseMoneyToMinor(text, currency, locale),
    (text) => percentToBp(text, locale),
  );
  const rests = rows.filter((row) => row.mode === 'rest').length;
  const placed = previews.reduce<number>((sum, value) => sum + (value ?? 0), 0);
  const left = total - placed;
  const invalid =
    rows.length === 0 ||
    rests > 1 ||
    previews.some((value) => value === null) ||
    rows.some((row) => row.target === '') ||
    left !== 0;
  const money = (minor: number) => formatMoneyMinor(minor, currency, locale);
  const nameOf = (target: string) =>
    target === UNASSIGNED
      ? t('virtualAccount.unassigned')
      : (envelopes.find((envelope) => String(envelope.id) === target)?.name ??
        line.allocations.find((part) => String(part.envelopeId) === target)?.envelopeName ??
        '');
  const active = categories.filter((category) => category.archivedAt === null);
  const offered = (current: string) => [
    ...active,
    ...categories.filter(
      (category) => category.archivedAt !== null && String(category.id) === current,
    ),
  ];

  const patch = (key: string, next: Partial<SplitRow>) =>
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...next } : row)));

  async function handleSave() {
    if (invalid || submitting) return;
    setSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      const parts: SplitPartRequest[] = rows.map((row) => {
        const target =
          row.target === UNASSIGNED
            ? ({ unassigned: true } as const)
            : { envelopeId: Number(row.target) };
        const slice =
          row.mode === 'rest'
            ? ({ remainder: true } as const)
            : row.mode === 'percent'
              ? { percentBp: percentToBp(row.value, locale) ?? 0 }
              : {
                  amountMinor:
                    (total < 0 ? -1 : 1) * (parseMoneyToMinor(row.value, currency, locale) ?? 0),
                };
        return {
          ...target,
          ...slice,
          ...(row.category === NONE
            ? { uncategorized: true as const }
            : { categoryId: Number(row.category) }),
        };
      });
      await allocationsApi.split(virtualAccount.id, line.id, parts);
      await onSaved();
      onClose();
    } catch (caught) {
      const code = isApiError(caught) ? caught.code : ErrorCodes.UNKNOWN;
      if (code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        onClose();
        onSubscriptionRequired();
        return;
      }
      if (code === 'SPLIT_TOTAL_CHANGED') {
        const fresh = await onStale().catch(() => null);
        if (fresh !== null) {
          const next = seed(fresh, currency, locale);
          setLine(fresh);
          setBaseline(next);
          setRows(next);
        }
        setNotice(t('virtualAccount.split.changed'));
      } else {
        setError(errorCopy(t, caught, 'virtualAccount.errorGeneric'));
      }
      guard.focusErrors();
      setSubmitting(false);
    }
  }

  if (adding !== null) {
    return (
      <GuardedDialog
        guard={guard}
        titleId={addTitleId}
        subtitleId={addSubtitleId}
        fullWidth
        fullScreen={fullScreen}
        maxWidth="sm"
      >
        <AddCategoryForm
          virtualAccountId={virtualAccount.id}
          titleId={addTitleId}
          subtitleId={addSubtitleId}
          draft={adding}
          onDraft={setAdding}
          onBusy={setAddBusy}
          onCancel={() => setAdding(null)}
          onAdded={async (category) => {
            await onCategoryAdded(category);
            setAdding(null);
          }}
          onSubscriptionRequired={() => {
            onClose();
            onSubscriptionRequired();
          }}
        />
      </GuardedDialog>
    );
  }

  const account = line.account;
  const meta = [
    line.payee,
    formatCivilDate(line.postedOn, locale),
    account === null
      ? null
      : account.last4 === null
        ? account.name
        : t('virtualAccount.ledger.source', { account: account.name, last4: account.last4 }),
  ]
    .filter((bit): bit is string => bit !== null && bit.length > 0)
    .join(' · ');

  return (
    <GuardedDialog
      guard={guard}
      titleId={titleId}
      subtitle={<DialogSubtitle id={subtitleId}>{meta}</DialogSubtitle>}
      subtitleId={subtitleId}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="sm"
    >
      <DialogTitle id={titleId}>{t('virtualAccount.split.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          {rows.length > 1 && (
            <Typography sx={{ fontSize: 12, color: tokens.textLabel('surfaceRaised') }}>
              {t('virtualAccount.split.multi')}
            </Typography>
          )}
          {notice !== null && <Alert severity="warning">{notice}</Alert>}
          {error !== null && <Alert severity="error">{error}</Alert>}
          {rows.map((row, index) => {
            const name = nameOf(row.target);
            const colour =
              row.target === UNASSIGNED || row.target === ''
                ? tokens.text3
                : categoricalFor(Number(row.target), tokens).line;
            const preview = previews[index];
            return (
              <Box
                key={row.key}
                data-split-row=""
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) minmax(0, 180px)' },
                  gap: 1.25,
                  alignItems: 'center',
                  pt: 1.25,
                  borderTop: `1px solid ${tokens.line}`,
                }}
              >
                <Stack direction="row" sx={{ alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <Box
                    component="span"
                    aria-hidden
                    sx={{
                      flex: '0 0 8px',
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: colour,
                    }}
                  />
                  <FormControl size="small" fullWidth>
                    <InputLabel id={`${row.key}-target`}>{t('allocate.rowDestination')}</InputLabel>
                    <Select
                      labelId={`${row.key}-target`}
                      label={t('allocate.rowDestination')}
                      value={row.target}
                      onChange={(event) => patch(row.key, { target: event.target.value })}
                    >
                      <MenuItem value={UNASSIGNED}>{t('virtualAccount.unassigned')}</MenuItem>
                      {envelopes.map((envelope) => (
                        <MenuItem key={envelope.id} value={String(envelope.id)}>
                          {envelope.name}
                        </MenuItem>
                      ))}
                      {row.target !== UNASSIGNED &&
                        row.target !== '' &&
                        !envelopes.some((envelope) => String(envelope.id) === row.target) && (
                          <MenuItem value={row.target}>{name}</MenuItem>
                        )}
                    </Select>
                  </FormControl>
                </Stack>
                <FormControl size="small" fullWidth>
                  <InputLabel id={`${row.key}-category`}>{t('envelope.colCategory')}</InputLabel>
                  <Select
                    labelId={`${row.key}-category`}
                    label={t('envelope.colCategory')}
                    value={row.category}
                    onChange={(event) => patch(row.key, { category: event.target.value })}
                  >
                    <MenuItem value={NONE}>{t('envelope.uncategorized')}</MenuItem>
                    {offered(row.category).map((category) => (
                      <MenuItem key={category.id} value={String(category.id)}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Stack
                  direction="row"
                  sx={{ gridColumn: '1 / -1', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                >
                  <ModeToggle
                    label={name}
                    value={row.mode}
                    onChange={(mode) => patch(row.key, { mode })}
                  />
                  {row.mode === 'rest' ? (
                    <Typography
                      data-rest-preview=""
                      sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', minWidth: 120 }}
                    >
                      {preview === null || preview === undefined ? (
                        '—'
                      ) : (
                        <MoneyParts minor={preview} currency={currency} />
                      )}
                    </Typography>
                  ) : (
                    <TextField
                      size="small"
                      value={row.value}
                      onChange={(event) => patch(row.key, { value: event.target.value })}
                      error={preview === null && row.value.trim().length > 0}
                      slotProps={{
                        htmlInput: {
                          inputMode: 'decimal',
                          'aria-label': t(
                            row.mode === 'percent'
                              ? 'virtualAccount.split.percentFor'
                              : 'virtualAccount.split.amountFor',
                            { destination: name },
                          ),
                          style: { textAlign: 'end' },
                        },
                      }}
                      sx={{ width: 120 }}
                    />
                  )}
                  {row.mode === 'percent' && preview !== null && preview !== undefined && (
                    <Typography sx={{ fontSize: 12, color: tokens.text2 }}>
                      <MoneyParts minor={preview} currency={currency} />
                    </Typography>
                  )}
                  <Box sx={{ flex: 1 }} />
                  {rows.length > 1 && (
                    <IconButton
                      size="small"
                      aria-label={t('virtualAccount.split.removeRow', { destination: name })}
                      onClick={() => setRows((prev) => prev.filter((r) => r.key !== row.key))}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </Stack>
              </Box>
            );
          })}
          <Box>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => {
                setRows((prev) => [
                  ...prev,
                  {
                    key: `new-${nextKey}`,
                    target: UNASSIGNED,
                    mode: 'amount',
                    value: '',
                    category: NONE,
                  },
                ]);
                setNextKey((n) => n + 1);
              }}
            >
              {`+ ${t('allocate.addRow')}`}
            </Link>
          </Box>
          <Box
            role="status"
            aria-live="polite"
            data-split-totals=""
            sx={{ fontSize: 13, color: tokens.text2, fontVariantNumeric: 'tabular-nums' }}
          >
            <Typography component="p" sx={{ fontSize: 13 }}>
              {t('virtualAccount.split.total', { name: virtualAccount.name, amount: money(total) })}
            </Typography>
            <Typography
              component="p"
              sx={{ fontSize: 13, color: left === 0 ? tokens.text2 : tokens.errorText }}
            >
              {rests > 1
                ? t('allocate.oneRest')
                : t('virtualAccount.split.left', { amount: money(left) })}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{ justifyContent: 'space-between', borderTop: `1px solid ${tokens.line}` }}
      >
        <Link
          component="button"
          type="button"
          variant="body2"
          sx={{ fontWeight: 500, marginInlineStart: 1 }}
          onClick={() => setAdding(EMPTY_CATEGORY_DRAFT)}
        >
          {`+ ${t('virtualAccount.split.newCategory')}`}
        </Link>
        <Stack direction="row" sx={{ gap: 1 }}>
          <Button onClick={guard.requestClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <SubmitButton onClick={() => void handleSave()} disabled={invalid} loading={submitting}>
            {t('common.save')}
          </SubmitButton>
        </Stack>
      </DialogActions>
    </GuardedDialog>
  );
}
